/**
 * CalendarTabPanel — single shared component for both AgentUsers and
 * PlaygroundChat pages. Mounts on activeTab === 'calendar'.
 *
 * BE 등록 여부는 BE 응답 실측으로 분기 (§1.2 / §5.3 of design doc):
 *   - 200 → @schedule-x/react 월/주 그리드
 *   - 404 USER_NOT_BE_REGISTERED → BE 미연동 안내
 *   - other → 에러 패널
 *
 * PR 3 scope: read-only month grid + timezone toggle + click → console
 * (modal lands in PR 4). props remain `userId: string` only — no per-page
 * branching (mounted identically in AgentUsers and PlaygroundChat).
 */

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, AlertCircle, Loader2, Plus } from 'lucide-react';
import { AxiosError } from 'axios';
import { ScheduleXCalendar, useCalendarApp } from '@schedule-x/react';
import {
  createViewMonthGrid,
  createViewMonthAgenda,
  createViewWeek,
  type CalendarEventExternal,
} from '@schedule-x/calendar';
import { createEventsServicePlugin } from '@schedule-x/events-service';
import '@schedule-x/theme-default/dist/index.css';
// schedule-x bundles its own Temporal polyfill. Importing the /global
// entrypoint of the same package installs the polyfill onto globalThis so
// the two classes are identity-equal (avoids the cross-bundle instanceof
// mismatch that caused "Event start time needs to be a Temporal.ZonedDateTime").
import 'temporal-polyfill/global';

import { calendarApi, getCalendarErrorEnvelope } from '../api/calendar';
import type {
  CalendarDailyEvent,
  CalendarEvent,
  CalendarEventCreate,
  CalendarEventUpdate,
  DailyEventGroups,
} from '../api/calendar';
import { usersApi, type UserDetail } from '../api/users';
import CalendarEventModal, { type ModalMode } from './CalendarEventModal';

interface CalendarTabPanelProps {
  userId: string;
}

const KST_OFFSET = '+09:00';
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Grid bounds — 6-week window covering the given month (Sun..Sat padded). */
function gridBoundsFor(year: number, monthZeroBased: number): { start: string; end: string } {
  const first = new Date(year, monthZeroBased, 1);
  const last = new Date(year, monthZeroBased + 1, 0);
  first.setDate(first.getDate() - first.getDay());
  last.setDate(last.getDate() + (6 - last.getDay()));
  const toRfc = (d: Date, h: number, m: number, s: number) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(h)}:${pad(m)}:${pad(s)}${KST_OFFSET}`;
  return { start: toRfc(first, 0, 0, 0), end: toRfc(last, 23, 59, 59) };
}

/** schedule-x v4 requires Temporal.ZonedDateTime instances (not strings).
 *  Uses the polyfill installed on globalThis. */
function projectToTz(iso: string, tz: string): Temporal.ZonedDateTime {
  const epochMs = new Date(iso).getTime();
  return Temporal.Instant.fromEpochMilliseconds(epochMs).toZonedDateTimeISO(tz);
}

function colorFor(calendarType: string): string {
  switch (calendarType) {
    case 'TRAINING_PLAN':
      return '#2196F3';
    case 'RUNNING_RECORD':
      return '#9E9E9E';
    case 'CUSTOM':
      return '#4CAF50';
    default:
      return '#607D8B';
  }
}

function flattenGroups(
  groups: DailyEventGroups,
  tz: string,
): CalendarEventExternal[] {
  const seen = new Set<number>();
  const out: CalendarEventExternal[] = [];
  for (const day of Object.keys(groups)) {
    for (const ev of groups[day] as CalendarDailyEvent[]) {
      if (seen.has(ev.event_id)) continue; // BE duplicates multi-day events per date — dedupe by id
      seen.add(ev.event_id);
      out.push({
        id: String(ev.event_id),
        title: ev.summary,
        start: projectToTz(ev.startDt, tz),
        end: ev.endDt ? projectToTz(ev.endDt, tz) : projectToTz(ev.startDt, tz),
        calendarId: ev.calendarType.toLowerCase(),
      });
    }
  }
  return out;
}

const CalendarTabPanel: React.FC<CalendarTabPanelProps> = ({ userId }) => {
  // ── Month navigation state ────────────────────────────────────────────
  const [cursor, setCursor] = React.useState<{ year: number; month: number }>(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  const { start, end } = React.useMemo(
    () => gridBoundsFor(cursor.year, cursor.month),
    [cursor.year, cursor.month],
  );

  // ── Timezone toggle ───────────────────────────────────────────────────
  const userQuery = useQuery({
    queryKey: ['user-detail', userId],
    queryFn: () => usersApi.getById(userId),
    staleTime: 60_000,
  });
  const userTz = React.useMemo<string | null>(() => {
    const u = userQuery.data as UserDetail | undefined;
    const dev = u?.device as Record<string, unknown> | undefined;
    const tz = dev?.timezone;
    return typeof tz === 'string' && tz.length > 0 ? tz : null;
  }, [userQuery.data]);
  // Default to the user's timezone (what they themselves see in the BE app).
  // Falls back to KST (BE storage standard) when user.device.timezone missing,
  // then to the operator's browser TZ. Operator can override via toggle.
  const FALLBACK_USER_TZ = 'Asia/Seoul';
  const [displayTz, setDisplayTz] = React.useState<string>(FALLBACK_USER_TZ);
  React.useEffect(() => {
    setDisplayTz(userTz ?? FALLBACK_USER_TZ);
  }, [userId, userTz]);

  // ── Events query ──────────────────────────────────────────────────────
  const eventsQuery = useQuery<DailyEventGroups, AxiosError>({
    queryKey: ['calendar', 'grouped-daily', userId, start, end],
    queryFn: () => calendarApi.listGroupedDaily(userId, start, end),
    retry: (failureCount, err) => {
      const status = err.response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
    staleTime: 30_000,
  });

  const flatEvents = React.useMemo(
    () => (eventsQuery.data ? flattenGroups(eventsQuery.data, displayTz) : []),
    [eventsQuery.data, displayTz],
  );

  // ── Modal + mutations ────────────────────────────────────────────────
  const queryClient = useQueryClient();
  const [modal, setModal] = React.useState<{
    open: boolean;
    mode: ModalMode;
    eventId: number | null;
  }>({ open: false, mode: 'create', eventId: null });
  const [mutationError, setMutationError] = React.useState<string | null>(null);

  const calendarKey: readonly unknown[] = ['calendar', 'grouped-daily', userId];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: calendarKey });

  const detailQuery = useQuery({
    queryKey: ['calendar', 'event', userId, modal.eventId],
    queryFn: () => calendarApi.getEvent(userId, modal.eventId as number),
    enabled: modal.open && modal.eventId !== null && modal.mode !== 'create',
    staleTime: 10_000,
  });

  const createMut = useMutation({
    mutationFn: (body: CalendarEventCreate) => calendarApi.createEvent(userId, body),
    onSuccess: async () => {
      await invalidate();
      setModal({ open: false, mode: 'create', eventId: null });
      setMutationError(null);
    },
    onError: (e) => {
      const env = getCalendarErrorEnvelope(e);
      setMutationError(env?.msg || (e as Error).message);
    },
  });
  const updateMut = useMutation({
    mutationFn: (body: CalendarEventUpdate) =>
      calendarApi.updateEvent(userId, modal.eventId as number, body),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ['calendar', 'event', userId, modal.eventId] });
      setModal((m) => ({ ...m, mode: 'view' }));
      setMutationError(null);
    },
    onError: (e) => {
      const env = getCalendarErrorEnvelope(e);
      setMutationError(env?.msg || (e as Error).message);
    },
  });
  const deleteMut = useMutation({
    mutationFn: () => calendarApi.deleteEvent(userId, modal.eventId as number),
    onSuccess: async () => {
      await invalidate();
      setModal({ open: false, mode: 'create', eventId: null });
      setMutationError(null);
    },
    onError: (e) => {
      const env = getCalendarErrorEnvelope(e);
      setMutationError(env?.msg || (e as Error).message);
    },
  });

  const closeModal = React.useCallback(() => {
    setModal({ open: false, mode: 'create', eventId: null });
    setMutationError(null);
  }, []);

  // ── schedule-x calendar app ──────────────────────────────────────────
  // `plugins` is the SECOND argument of useCalendarApp, NOT a config key.
  // Putting it inside the config object causes a runtime ErrorBoundary fire.
  const eventsService = React.useMemo(() => createEventsServicePlugin(), []);
  const calendar = useCalendarApp(
    {
      views: [createViewMonthGrid(), createViewMonthAgenda(), createViewWeek()],
      defaultView: createViewMonthGrid().name,
      firstDayOfWeek: 7, // 7 = Sunday (per schedule-x WeekDay enum; default is 1 = Monday)
      // CRITICAL: schedule-x renders events in its OWN configured timezone,
      // not the timezone embedded in ZonedDateTime instances. Default is UTC,
      // which is why "오후 8시 KST" was showing as "오전 11시 UTC" before.
      timezone: displayTz,
      events: flatEvents,
      calendars: {
        training_plan: {
          colorName: 'training_plan',
          lightColors: { main: colorFor('TRAINING_PLAN'), container: '#E3F2FD', onContainer: '#0D47A1' },
          darkColors: { main: colorFor('TRAINING_PLAN'), container: '#0D47A1', onContainer: '#E3F2FD' },
        },
        running_record: {
          colorName: 'running_record',
          lightColors: { main: colorFor('RUNNING_RECORD'), container: '#ECEFF1', onContainer: '#37474F' },
          darkColors: { main: colorFor('RUNNING_RECORD'), container: '#37474F', onContainer: '#ECEFF1' },
        },
        custom: {
          colorName: 'custom',
          lightColors: { main: colorFor('CUSTOM'), container: '#E8F5E9', onContainer: '#1B5E20' },
          darkColors: { main: colorFor('CUSTOM'), container: '#1B5E20', onContainer: '#E8F5E9' },
        },
      },
      callbacks: {
        onEventClick: (e) => {
          const id = Number(e.id);
          if (!Number.isNaN(id)) {
            setModal({ open: true, mode: 'view', eventId: id });
            setMutationError(null);
          }
        },
      },
    },
    [eventsService],
  );

  // Push fresh events to schedule-x when the query result changes.
  React.useEffect(() => {
    eventsService.set(flatEvents);
  }, [flatEvents, eventsService]);

  // ── 404 with envelope: BE 미연동 안내 ──────────────────────────────────
  if (eventsQuery.isError) {
    const envelope = getCalendarErrorEnvelope(eventsQuery.error);
    if (envelope?.error_code === 'USER_NOT_BE_REGISTERED') {
      return (
        <div className="flex h-full items-center justify-center p-8">
          <div className="max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
            <CalendarDays size={32} className="mx-auto mb-3 text-amber-600" />
            <h3 className="mb-2 text-base font-semibold text-amber-900">BE 미연동 유저</h3>
            <p className="text-sm leading-relaxed text-amber-800">
              이 유저는 BE <code className="rounded bg-amber-100 px-1">tbl_user</code>에 등록되지
              않아 캘린더 API를 호출할 수 없습니다.
            </p>
            <p className="mt-2 text-xs text-amber-700">
              실 유저로 검증하거나, BE에 테스트 유저를 등록한 뒤 ai_user_id를 매핑해 사용하세요.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <AlertCircle size={32} className="mx-auto mb-3 text-red-600" />
          <h3 className="mb-2 text-base font-semibold text-red-900">캘린더를 불러오지 못했습니다</h3>
          <p className="text-sm text-red-800">
            {envelope?.msg || eventsQuery.error.message || 'Unknown error'}
          </p>
          {envelope?.error_code && (
            <p className="mt-2 font-mono text-xs text-red-700">{envelope.error_code}</p>
          )}
        </div>
      </div>
    );
  }

  if (eventsQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-slate-400">
        <Loader2 size={20} className="mr-2 animate-spin" />
        캘린더 로딩 중…
      </div>
    );
  }

  // ── Toolbar + grid ────────────────────────────────────────────────────
  const monthLabel = `${cursor.year}.${pad(cursor.month + 1)}`;
  const tzToggleDisabled = !userTz;
  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-3 flex items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2">
          <button
            data-testid="cal-prev-month"
            onClick={() =>
              setCursor((c) =>
                c.month === 0
                  ? { year: c.year - 1, month: 11 }
                  : { year: c.year, month: c.month - 1 },
              )
            }
            className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50"
          >
            ←
          </button>
          <span data-testid="cal-month-label" className="font-medium">
            {monthLabel}
          </span>
          <button
            data-testid="cal-next-month"
            onClick={() =>
              setCursor((c) =>
                c.month === 11
                  ? { year: c.year + 1, month: 0 }
                  : { year: c.year, month: c.month + 1 },
              )
            }
            className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50"
          >
            →
          </button>
          <button
            data-testid="cal-today"
            onClick={() => {
              const n = new Date();
              setCursor({ year: n.getFullYear(), month: n.getMonth() });
            }}
            className="ml-2 rounded border border-slate-200 px-2 py-1 hover:bg-slate-50"
          >
            오늘
          </button>
          <button
            data-testid="cal-create"
            onClick={() => {
              setModal({ open: true, mode: 'create', eventId: null });
              setMutationError(null);
            }}
            className="ml-2 inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
          >
            <Plus size={12} />
            이벤트
          </button>
        </div>
        <div
          className="flex items-center gap-2 text-xs text-slate-500"
          data-testid="cal-tz-toggle"
        >
          <span>시간대:</span>
          <button
            onClick={() => setDisplayTz(userTz ?? FALLBACK_USER_TZ)}
            className={`rounded px-2 py-1 ${displayTz === (userTz ?? FALLBACK_USER_TZ) ? 'bg-slate-200 text-slate-800' : 'hover:bg-slate-100'}`}
            title={tzToggleDisabled ? `User timezone 미설정 → ${FALLBACK_USER_TZ} 기본` : ''}
          >
            User ({userTz ?? `${FALLBACK_USER_TZ} 기본`})
          </button>
          <button
            onClick={() => setDisplayTz(BROWSER_TZ)}
            className={`rounded px-2 py-1 ${displayTz === BROWSER_TZ ? 'bg-slate-200 text-slate-800' : 'hover:bg-slate-100'}`}
          >
            Browser ({BROWSER_TZ})
          </button>
        </div>
      </div>
      {/* Grid + inline detail panel (split when modal.open) */}
      <div className={modal.open ? 'flex h-full flex-col' : 'flex-1 overflow-auto'} data-testid="cal-grid">
        <div className={modal.open ? 'h-1/2 overflow-auto' : 'h-full'}>
          <ScheduleXCalendar calendarApp={calendar} />
        </div>
        {modal.open && (
          <div className="h-1/2 overflow-auto">
            <CalendarEventModal
              open={modal.open}
              mode={modal.mode}
              event={
                modal.mode === 'create'
                  ? null
                  : (detailQuery.data as CalendarEvent | undefined) ?? null
              }
              busy={createMut.isPending || updateMut.isPending || deleteMut.isPending}
              errorMessage={mutationError}
              onClose={closeModal}
              onModeChange={(m) => setModal((cur) => ({ ...cur, mode: m }))}
              onSubmitCreate={(body) => createMut.mutate(body)}
              onSubmitUpdate={(body) => updateMut.mutate(body)}
              onDelete={() => deleteMut.mutate()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarTabPanel;
